import { useEffect, useMemo, useState } from "react";
import { http, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import SearchableSelect from "@/components/SearchableSelect";
import MultiSearchableSelect from "@/components/MultiSearchableSelect";
import { useSortableTable, SortIndicator } from "@/hooks/useSortableTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { DownloadSimple, FilePdf, ChartBar } from "@phosphor-icons/react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function defaultRange() {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);
    return {
        from: start.toISOString().slice(0, 10),
        to: end.toISOString().slice(0, 10),
    };
}

const TABS = [
    { key: "detail", label: "Detallado" },
    { key: "project-person", label: "Por proyecto y persona" },
];

export default function Reports() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const isPmOrAdmin = user?.role === "admin" || user?.role === "pm";
    const range = defaultRange();
    const [tab, setTab] = useState("detail");
    const [filters, setFilters] = useState({
        date_from: range.from,
        date_to: range.to,
        user_id: "",
        project_id: [],
        customer_id: "",
    });
    const [data, setData] = useState(null);
    const [ppData, setPpData] = useState(null);
    const [users, setUsers] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        http.get("/customers").then((r) => setCustomers(r.data));
        http.get("/projects").then((r) => setProjects(r.data));
        if (isAdmin) http.get("/users").then((r) => setUsers(r.data));
    }, [isAdmin]);

    const run = async () => {
        setBusy(true);
        try {
            const projectCsv = (filters.project_id || []).join(",");
            const params = { ...filters };
            delete params.project_id;
            Object.keys(params).forEach((k) => {
                if (!params[k]) delete params[k];
            });
            if (projectCsv) params.project_id = projectCsv;
            if (tab === "detail") {
                const res = await http.get("/reports", { params });
                setData(res.data);
            } else {
                // Project & Person acepta date_from/date_to + customer_id + project_id + user_id
                const p2 = {
                    date_from: params.date_from,
                    date_to: params.date_to,
                };
                if (params.customer_id) p2.customer_id = params.customer_id;
                if (projectCsv) p2.project_id = projectCsv;
                if (params.user_id) p2.user_id = params.user_id;
                const res = await http.get("/reports/by-project-person", {
                    params: p2,
                });
                setPpData(res.data);
            }
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const csvUrl = () => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => {
            if (k === "project_id") return;
            if (v) params.set(k, v);
        });
        const projectCsv = (filters.project_id || []).join(",");
        if (projectCsv) params.set("project_id", projectCsv);
        return `${API}/reports/export.csv?${params.toString()}`;
    };

    const csvUrlPp = () => {
        const params = new URLSearchParams();
        params.set("date_from", filters.date_from);
        params.set("date_to", filters.date_to);
        if (filters.customer_id) params.set("customer_id", filters.customer_id);
        const projectCsv = (filters.project_id || []).join(",");
        if (projectCsv) params.set("project_id", projectCsv);
        if (filters.user_id) params.set("user_id", filters.user_id);
        return `${API}/reports/by-project-person/export.csv?${params.toString()}`;
    };

    const exportPdf = () => {
        if (!data) return;
        const doc = new jsPDF({ orientation: "landscape", unit: "pt" });
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.text("Ingartek Portal — Informe", 40, 40);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.text(
            `Periodo: ${filters.date_from} a ${filters.date_to}   ·   Total: ${data.total_hours.toFixed(2)} h`,
            40,
            58,
        );
        autoTable(doc, {
            startY: 80,
            head: [["Fecha", "Empleado", "Cliente", "Proyecto", "Código", "Horas", "Comentario"]],
            body: data.rows.map((r) => [
                r.entry_date,
                r.user_name,
                r.customer,
                r.project,
                r.project_code || "",
                r.hours.toFixed(2),
                r.comment || "",
            ]),
            styles: { fontSize: 9 },
            headStyles: { fillColor: [2, 6, 23] },
        });
        doc.save(`reporte_${filters.date_from}_${filters.date_to}.pdf`);
    };

    const customerOptions = customers.filter((c) => c.active !== false);
    const projectOptions = projects.filter(
        (p) => !filters.customer_id || p.customer_id === filters.customer_id,
    );

    return (
        <div data-testid="page-reports">
            <PageHeader
                eyebrow="Informes"
                title="Análisis de horas"
                description="Filtra por fecha, cliente, proyecto y usuario. Exporta a CSV o PDF."
            />
            <div className="p-6 lg:p-10 space-y-6">
                {isPmOrAdmin && (
                    <div className="border-b border-slate-200 flex gap-1" data-testid="report-tabs">
                        {TABS.map((t) => (
                            <button
                                key={t.key}
                                onClick={() => setTab(t.key)}
                                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                                    tab === t.key
                                        ? "border-[#002BAF] text-[#002BAF]"
                                        : "border-transparent text-slate-500 hover:text-slate-800"
                                }`}
                                data-testid={`report-tab-${t.key}`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                )}

                <div className="border border-slate-200 bg-white p-5 grid grid-cols-2 lg:grid-cols-6 gap-3 items-end">
                    <div>
                        <Label className="text-xs">Desde</Label>
                        <Input
                            type="date"
                            value={filters.date_from}
                            onChange={(e) =>
                                setFilters({ ...filters, date_from: e.target.value })
                            }
                            className="mt-1 rounded-sm border-slate-300 font-mono"
                            data-testid="report-from"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Hasta</Label>
                        <Input
                            type="date"
                            value={filters.date_to}
                            onChange={(e) =>
                                setFilters({ ...filters, date_to: e.target.value })
                            }
                            className="mt-1 rounded-sm border-slate-300 font-mono"
                            data-testid="report-to"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Cliente</Label>
                        <SearchableSelect
                            value={filters.customer_id || ""}
                            onChange={(v) =>
                                setFilters({
                                    ...filters,
                                    customer_id: v,
                                    project_id: [],
                                })
                            }
                            options={customerOptions.map((c) => ({
                                value: c.customer_id,
                                label: c.name,
                                hint: c.code || "",
                            }))}
                            placeholder="Todos"
                            className="mt-1"
                            data-testid="report-customer"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Proyecto</Label>
                        <MultiSearchableSelect
                            value={filters.project_id || []}
                            onChange={(v) =>
                                setFilters({
                                    ...filters,
                                    project_id: v,
                                })
                            }
                            options={projectOptions.map((p) => ({
                                value: p.project_id,
                                label: p.name,
                                hint: p.project_code || "",
                            }))}
                            placeholder="Todos"
                            className="mt-1"
                            data-testid="report-project"
                        />
                    </div>
                    {isAdmin && (
                        <div>
                            <Label className="text-xs">Empleado</Label>
                            <SearchableSelect
                                value={filters.user_id || ""}
                                onChange={(v) =>
                                    setFilters({
                                        ...filters,
                                        user_id: v,
                                    })
                                }
                                options={users
                                    .filter((u) => !u.archived)
                                    .map((u) => ({
                                        value: u.user_id,
                                        label: u.name,
                                    }))}
                                placeholder="Todos"
                                className="mt-1"
                                data-testid="report-user"
                            />
                        </div>
                    )}
                    <div className="flex gap-2">
                        <Button
                            onClick={run}
                            disabled={busy}
                            className="rounded-sm bg-[#002BAF] hover:bg-[#001F82] flex-1"
                            data-testid="report-run"
                        >
                            <ChartBar size={14} className="mr-2" /> Generar
                        </Button>
                    </div>
                </div>

                {tab === "detail" && data && (
                    <>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                            <div className="bg-white p-6">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                    Total horas
                                </div>
                                <div className="font-mono text-3xl font-bold mt-2">
                                    {data.total_hours.toFixed(2)}
                                </div>
                            </div>
                            <div className="bg-white p-6">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
                                    Por proyecto
                                </div>
                                <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="report-by-project">
                                    {Object.entries(data.by_project)
                                        .sort((a, b) => b[1] - a[1])
                                        .map(([k, v]) => (
                                            <div
                                                key={k}
                                                className="flex justify-between text-sm"
                                            >
                                                <span className="truncate text-slate-700">
                                                    {k}
                                                </span>
                                                <span className="font-mono tabular-nums text-slate-900 font-medium">
                                                    {v.toFixed(2)}
                                                </span>
                                            </div>
                                        ))}
                                </div>
                            </div>
                            <div className="bg-white p-6">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
                                    Por empleado
                                </div>
                                <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="report-by-user">
                                    {Object.entries(data.by_user)
                                        .sort((a, b) => b[1] - a[1])
                                        .map(([k, v]) => (
                                            <div
                                                key={k}
                                                className="flex justify-between text-sm"
                                            >
                                                <span className="truncate text-slate-700">
                                                    {k}
                                                </span>
                                                <span className="font-mono tabular-nums text-slate-900 font-medium">
                                                    {v.toFixed(2)}
                                                </span>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>

                        <div className="border border-slate-200 bg-white">
                            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                                <div className="font-display text-lg font-semibold">
                                    Detalle ({data.rows.length} líneas)
                                </div>
                                <div className="flex gap-2">
                                    <a
                                        href={csvUrl()}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-sm flex items-center gap-1 px-3 py-2 border border-slate-300 hover:bg-slate-50 rounded-sm"
                                        data-testid="report-csv"
                                    >
                                        <DownloadSimple size={14} /> CSV
                                    </a>
                                    <button
                                        onClick={exportPdf}
                                        className="text-sm flex items-center gap-1 px-3 py-2 border border-slate-300 hover:bg-slate-50 rounded-sm"
                                        data-testid="report-pdf"
                                    >
                                        <FilePdf size={14} /> PDF
                                    </button>
                                </div>
                            </div>
                            <div className="overflow-x-auto max-h-[60vh]">
                                <ReportDetailTable rows={data.rows} />
                            </div>
                        </div>
                    </>
                )}

                {tab === "project-person" && ppData && (
                    <ProjectPersonReport
                        data={ppData}
                        csvUrl={csvUrlPp()}
                    />
                )}
            </div>
        </div>
    );
}

const ROLE_LABELS = { admin: "Admin", pm: "PM", consultant: "Empleado" };

function fmtEuro(n) {
    return (
        (n || 0)
            .toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            .replace(/\s/g, " ") + " €"
    );
}

function fmtHours(n) {
    return (n || 0).toLocaleString("es-ES", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function ReportDetailTable({ rows }) {
    const accessors = useMemo(
        () => ({
            entry_date: (r) => r.entry_date || "",
            user_name: (r) => r.user_name || "",
            customer: (r) => r.customer || "",
            project: (r) => r.project || "",
            hours: (r) => Number(r.hours || 0),
        }),
        [],
    );
    const { sorted, sortKey, sortDir, headerProps } = useSortableTable(
        rows,
        "report-detail",
        accessors,
        { initialKey: "entry_date", initialDir: "desc" },
    );
    return (
        <table className="w-full text-sm" data-testid="report-table">
            <thead className="sticky top-0 bg-slate-50">
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    <th {...headerProps("entry_date", "py-3 px-4 text-left")}>
                        Fecha
                        <SortIndicator active={sortKey === "entry_date"} dir={sortDir} />
                    </th>
                    <th {...headerProps("user_name", "py-3 px-4 text-left")}>
                        Empleado
                        <SortIndicator active={sortKey === "user_name"} dir={sortDir} />
                    </th>
                    <th {...headerProps("customer", "py-3 px-4 text-left")}>
                        Cliente
                        <SortIndicator active={sortKey === "customer"} dir={sortDir} />
                    </th>
                    <th {...headerProps("project", "py-3 px-4 text-left")}>
                        Proyecto
                        <SortIndicator active={sortKey === "project"} dir={sortDir} />
                    </th>
                    <th {...headerProps("hours", "py-3 px-4 text-right")}>
                        Horas
                        <SortIndicator active={sortKey === "hours"} dir={sortDir} />
                    </th>
                    <th className="py-3 px-4 text-left">Comentario</th>
                </tr>
            </thead>
            <tbody>
                {sorted.length === 0 && (
                    <tr>
                        <td colSpan={6} className="text-center py-12 text-slate-500">
                            Sin resultados.
                        </td>
                    </tr>
                )}
                {sorted.map((r, i) => (
                    <tr
                        key={i}
                        className="border-b border-slate-100 hover:bg-slate-50"
                    >
                        <td className="py-2 px-4 font-mono text-slate-700">
                            {r.entry_date}
                        </td>
                        <td className="py-2 px-4 text-slate-800">{r.user_name}</td>
                        <td className="py-2 px-4 text-slate-700">{r.customer}</td>
                        <td className="py-2 px-4 text-slate-700">{r.project}</td>
                        <td className="py-2 px-4 text-right font-mono tabular-nums font-semibold">
                            {r.hours.toFixed(2)}
                        </td>
                        <td className="py-2 px-4 text-slate-600 max-w-md truncate">
                            {r.comment || ""}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function ProjectPersonReport({ data, csvUrl }) {
    const accessors = useMemo(
        () => ({
            customer: (r) => `${r.customer_code || ""} ${r.customer_name || ""}`,
            project_name: (r) => r.project_name || "",
            project_code: (r) => r.project_code || "",
            user_name: (r) => r.user_name || "",
            user_role: (r) => r.user_role || "",
            hourly_rate: (r) => Number(r.hourly_rate || 0),
            hours: (r) => Number(r.hours || 0),
            total_amount: (r) => Number(r.total_amount || 0),
        }),
        [],
    );
    const { sorted, sortKey, sortDir, headerProps } = useSortableTable(
        data.rows,
        "report-project-person",
        accessors,
        { initialKey: "customer", initialDir: "asc" },
    );
    return (
        <div data-testid="project-person-report" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                <div className="bg-white p-6">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        Total horas
                    </div>
                    <div className="font-mono text-3xl font-bold mt-2" data-testid="pp-total-hours">
                        {fmtHours(data.total_hours)}
                    </div>
                </div>
                <div className="bg-white p-6">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        Importe total
                    </div>
                    <div className="font-mono text-3xl font-bold mt-2" data-testid="pp-total-amount">
                        {fmtEuro(data.total_amount)}
                    </div>
                </div>
                <div className="bg-white p-6">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        Líneas
                    </div>
                    <div className="font-mono text-3xl font-bold mt-2">{data.rows.length}</div>
                </div>
            </div>

            <div className="border border-slate-200 bg-white">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                    <div className="font-display text-lg font-semibold">
                        Detalle por proyecto y persona
                    </div>
                    <a
                        href={csvUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm flex items-center gap-1 px-3 py-2 border border-slate-300 hover:bg-slate-50 rounded-sm"
                        data-testid="pp-csv"
                    >
                        <DownloadSimple size={14} /> CSV
                    </a>
                </div>
                <div className="overflow-x-auto max-h-[70vh]">
                    <table className="w-full text-sm" data-testid="pp-table">
                        <thead className="sticky top-0 bg-slate-50">
                            <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                                <th {...headerProps("customer", "py-3 px-4 text-left")}>
                                    Cliente
                                    <SortIndicator active={sortKey === "customer"} dir={sortDir} />
                                </th>
                                <th {...headerProps("project_name", "py-3 px-4 text-left")}>
                                    Proyecto
                                    <SortIndicator active={sortKey === "project_name"} dir={sortDir} />
                                </th>
                                <th {...headerProps("project_code", "py-3 px-4 text-left")}>
                                    Código de Proyecto
                                    <SortIndicator active={sortKey === "project_code"} dir={sortDir} />
                                </th>
                                <th {...headerProps("user_name", "py-3 px-4 text-left")}>
                                    Empleado
                                    <SortIndicator active={sortKey === "user_name"} dir={sortDir} />
                                </th>
                                <th {...headerProps("user_role", "py-3 px-4 text-left")}>
                                    Rol
                                    <SortIndicator active={sortKey === "user_role"} dir={sortDir} />
                                </th>
                                <th {...headerProps("hourly_rate", "py-3 px-4 text-right")}>
                                    Tarifa
                                    <SortIndicator active={sortKey === "hourly_rate"} dir={sortDir} />
                                </th>
                                <th {...headerProps("hours", "py-3 px-4 text-right")}>
                                    Horas
                                    <SortIndicator active={sortKey === "hours"} dir={sortDir} />
                                </th>
                                <th {...headerProps("total_amount", "py-3 px-4 text-right")}>
                                    Importe
                                    <SortIndicator active={sortKey === "total_amount"} dir={sortDir} />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={8}
                                        className="text-center py-12 text-slate-500"
                                    >
                                        Sin resultados.
                                    </td>
                                </tr>
                            )}
                            {sorted.map((r, i) => (
                                <tr
                                    key={i}
                                    className="border-b border-slate-100 hover:bg-slate-50 even:bg-blue-50/20"
                                    data-testid={`pp-row-${i}`}
                                >
                                    <td className="py-2 px-4 text-slate-700 whitespace-nowrap">
                                        {[r.customer_code, r.customer_name]
                                            .filter(Boolean)
                                            .join(" - ")}
                                    </td>
                                    <td className="py-2 px-4 text-slate-800">
                                        {r.project_name}
                                    </td>
                                    <td className="py-2 px-4 font-mono text-slate-600">
                                        {r.project_code}
                                    </td>
                                    <td className="py-2 px-4 text-slate-800">
                                        {r.user_name}
                                    </td>
                                    <td className="py-2 px-4 text-slate-600 text-xs">
                                        {ROLE_LABELS[r.user_role] || r.user_role}
                                    </td>
                                    <td className="py-2 px-4 text-right font-mono tabular-nums text-slate-700">
                                        {fmtEuro(r.hourly_rate)}
                                    </td>
                                    <td className="py-2 px-4 text-right font-mono tabular-nums font-semibold">
                                        {fmtHours(r.hours)}
                                    </td>
                                    <td className="py-2 px-4 text-right font-mono tabular-nums font-semibold">
                                        {fmtEuro(r.total_amount)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        {sorted.length > 0 && (
                            <tfoot className="sticky bottom-0 bg-slate-100 border-t-2 border-slate-300">
                                <tr>
                                    <td
                                        colSpan={6}
                                        className="py-3 px-4 text-right text-xs uppercase tracking-wider text-slate-500 font-bold"
                                    >
                                        Totales
                                    </td>
                                    <td className="py-3 px-4 text-right font-mono tabular-nums font-bold">
                                        {fmtHours(data.total_hours)}
                                    </td>
                                    <td className="py-3 px-4 text-right font-mono tabular-nums font-bold">
                                        {fmtEuro(data.total_amount)}
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
}
